import React, { useState, useEffect } from 'react';
import GameLayout from '../layout/GameLayout';
import FloatingCard from '../ui/FloatingCard';
import { apiClient } from '../../services/api/apiService';
import { getUserSports, getAllSports } from '../../services/api/sportsService';
import VenueSearch from '../ui/VenueSearch';
import SportMatchConfig from '../match/SportMatchConfig';
import { getSportDefaults, isTeamSport } from '../../config/sportsConfig';
import { getDatesForWeekday } from './scheduleAlgorithm';
import { toDateOnlyString } from '../../utils/dateOnly';
import { playFormatHelp } from './leagueFormat';
import { SKILL_LEVEL_OPTIONS, normalizeSkillLevel } from './leagueOptions';
import LeagueFeeFields, { feeForApi, feeFromLeague } from './LeagueFeeFields';
import './leagues.css';
import '../../styles/features/tournaments.css';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const LeagueEditForm = ({ league, sports: sportsProp = [], token, onCancel, onSuccess, openAccessModal }) => {
  const [loadedSports, setLoadedSports] = useState(sportsProp);

  useEffect(() => {
    const loadSports = async () => {
      try {
        const userRes = await getUserSports();
        const userSports = userRes?.data?.sports || userRes?.sports || userRes?.data || [];
        if (Array.isArray(userSports) && userSports.length > 0) {
          setLoadedSports(userSports);
          return;
        }
      } catch { /* ignore */ }
      try {
        const allRes = await getAllSports();
        const allSports = allRes?.data?.sports || allRes?.sports || allRes?.data || [];
        if (Array.isArray(allSports)) setLoadedSports(allSports);
      } catch { /* ignore */ }
    };
    loadSports();
  }, []);
  // Initialise game_days: prefer the new array field, fall back to wrapping legacy game_day
  const initGameDays = () => {
    if (Array.isArray(league.game_days) && league.game_days.length > 0) return league.game_days;
    if (league.game_day) return [league.game_day];
    return [];
  };

  const [formData, setFormData] = useState({
    name: league.name || '',
    sport_type: league.sport_type || '',
    sport_category: league.sport_category || 'Indoor',
    season: league.season || '',
    description: league.description || '',
    venue_name: league.venue_name || '',
    venue_address: league.venue_address || '',
    venue_lat: league.venue_lat || null,
    venue_lng: league.venue_lng || null,
    venue_place_id: league.venue_place_id || '',
    game_days: initGameDays(),
    start_time: league.start_time || '18:30',
    end_time: league.end_time || '21:00',
    number_of_courts: league.number_of_courts || 2,
    total_teams: league.total_teams || 6,
    games_per_team_per_day: league.games_per_team_per_day !== null && league.games_per_team_per_day !== undefined
      ? league.games_per_team_per_day
      : '',
    season_start_date: toDateOnlyString(league.season_start_date),
    season_end_date: toDateOnlyString(league.season_end_date),
    registration_deadline: toDateOnlyString(league.registration_deadline),
    playoff_format: league.playoff_format || 'single_elimination',
    is_public: league.is_public !== undefined ? league.is_public : true,
    skill_level: normalizeSkillLevel(league.skill_level),
    play_format: league.play_format || 'singles',
  });

  // League fee: kept apart from formData, which is sent field by field
  const [fee, setFee] = useState(() => feeFromLeague(league));
  const [gameRules, setGameRules] = useState(league.game_rules || {});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [formMessage, setFormMessage] = useState(null);

  // Detect schedule mode from existing data: ISO dates in game_days = specific_dates
  const isIsoDate = (s) => /^\d{4}-\d{2}-\d{2}$/.test(String(s));
  const existingDays = Array.isArray(league.game_days) && league.game_days.length > 0
    ? league.game_days
    : league.game_day ? [league.game_day] : [];
  const [scheduleMode, setScheduleMode] = useState(
    existingDays.length > 0 && existingDays.every(isIsoDate) ? 'specific_dates' : 'weekdays'
  );
  const [specificDates, setSpecificDates] = useState(
    existingDays.length > 0 && existingDays.every(isIsoDate) ? existingDays : []
  );

  const handleQuickPickDay = (day) => {
    const datesForDay = getDatesForWeekday(formData.season_start_date, formData.season_end_date, day);
    const allSel = datesForDay.length > 0 && datesForDay.every(d => specificDates.includes(d));
    if (allSel) {
      setSpecificDates(prev => prev.filter(d => !datesForDay.includes(d)));
    } else {
      setSpecificDates(prev => [...new Set([...prev, ...datesForDay])]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? (value === '' ? '' : parseInt(value) || 0) : value,
    }));
  };

  const handleDayToggle = (day) => {
    setFormData(prev => {
      const days = prev.game_days.includes(day)
        ? prev.game_days.filter(d => d !== day)
        : [...prev.game_days, day];
      return { ...prev, game_days: days };
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'League name is required';
    if (!formData.sport_type) newErrors.sport_type = 'Sport type is required';
    if (!formData.season_start_date) newErrors.season_start_date = 'Start date is required';
    if (!formData.season_end_date) newErrors.season_end_date = 'End date is required';
    if (formData.season_start_date && formData.season_end_date &&
        new Date(formData.season_start_date) >= new Date(formData.season_end_date)) {
      newErrors.season_end_date = 'End date must be after start date';
    }
    if (formData.total_teams < 2) newErrors.total_teams = 'Need at least 2 teams';
    if (formData.number_of_courts < 1) newErrors.number_of_courts = 'Need at least 1 court';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const sportOptions = loadedSports.map(sport => ({ value: sport.name, label: sport.name }));
  const isIndividualSport = formData.sport_type ? !isTeamSport(formData.sport_type) : false;
  const hasEntries = Number(league?.registered_teams || 0) > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    if (!league?.id) {
      setFormMessage({ type: 'error', text: 'Invalid league selected for editing.' });
      return;
    }
    const feeApi = feeForApi(fee);
    if (feeApi.error) {
      setFormMessage({ type: 'error', text: feeApi.error });
      return;
    }
    try {
      setLoading(true);
      const effectiveGameDays = scheduleMode === 'specific_dates'
        ? specificDates.sort()
        : formData.game_days;
      const payload = {
        ...formData,
        game_days: effectiveGameDays,
        games_per_team_per_day: formData.games_per_team_per_day === '' ? null : formData.games_per_team_per_day,
        game_day: effectiveGameDays.length > 0 ? effectiveGameDays[0] : null,
        game_rules: Object.keys(gameRules).length > 0
          ? gameRules
          : (formData.sport_type ? getSportDefaults(formData.sport_type) : null),
      };
      // Only send play_format when it was changed: it decides who enters the league
      // (players, pairs or teams), which the server locks once anyone has entered.
      if (payload.play_format === (league.play_format || 'singles')) {
        delete payload.play_format;
      }
      Object.assign(payload, feeApi.fields);
      await apiClient.put(`/leagues/${league.id}`, payload);
      setFormMessage({ type: 'success', text: 'League updated successfully.' });
      if (onSuccess) onSuccess('League updated successfully.');
    } catch (error) {
      if (error.response?.status === 403) {
        openAccessModal('edit leagues');
        return;
      }
      const errorMsg = error.response?.data?.message || error.message;
      setFormMessage({ type: 'error', text: `Failed to update league: ${errorMsg}` });
      console.error('Error updating league:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <GameLayout token={token} onLogout={() => {}}>
      <div className="tournament-creation-gaming league-creation-gaming">

        <FloatingCard className="header-card-gaming">
          <div className="header-content-gaming">
            <h2 className="tournament-title">✏️ Edit League</h2>
            <p className="tournament-subtitle">{league.name}</p>
          </div>
        </FloatingCard>

        {formMessage && (
          <FloatingCard className={`alert-card ${formMessage.type === 'error' ? 'alert-error-gaming' : 'alert-success-gaming'}`}>
            <div className="alert-content">
              <span className="alert-icon">{formMessage.type === 'error' ? '❌' : '✅'}</span>
              <div className="alert-message">{formMessage.text}</div>
            </div>
          </FloatingCard>
        )}

        <form onSubmit={handleSubmit} className="tournament-form-gaming league-form-gaming">

          {/* Basic Information */}
          <FloatingCard className="form-section-gaming">
            <h3 className="form-section-title">📋 Basic Information</h3>
            <div className="form-row-gaming">
              <div className="form-group-gaming">
                <label htmlFor="name" className="form-label-gaming">League Name *</label>
                <input type="text" id="name" name="name" value={formData.name} onChange={handleInputChange}
                  className={`form-input-gaming${errors.name ? ' input-error-gaming' : ''}`} />
                {errors.name && <span className="error-message-gaming">{errors.name}</span>}
              </div>
              <div className="form-group-gaming">
                <label htmlFor="sport_type" className="form-label-gaming">Sport *</label>
                <select id="sport_type" name="sport_type" value={formData.sport_type} onChange={handleInputChange}
                  className={`form-select-gaming${errors.sport_type ? ' input-error-gaming' : ''}`}>
                  <option value="">Select Sport</option>
                  {sportOptions.map(sport => (<option key={sport.value} value={sport.value}>{sport.label}</option>))}
                </select>
                {errors.sport_type && <span className="error-message-gaming">{errors.sport_type}</span>}
              </div>
            </div>
            <div className="form-row-gaming">
              <div className="form-group-gaming">
                <label htmlFor="season" className="form-label-gaming">Season</label>
                <input type="text" id="season" name="season" value={formData.season} onChange={handleInputChange}
                  className="form-input-gaming" placeholder="e.g., Fall 2024 (optional)" />
              </div>
              <div className="form-group-gaming">
                <label htmlFor="sport_category" className="form-label-gaming">Category</label>
                <select id="sport_category" name="sport_category" value={formData.sport_category} onChange={handleInputChange} className="form-select-gaming">
                  <option value="Indoor">🏠 Indoor</option>
                  <option value="Outdoor">🌿 Outdoor</option>
                </select>
              </div>
            </div>
            {/* The status is changed from the league page (Open / Close registration,
                Start, Complete, Cancel), which follows the lifecycle (LG-B10). */}
            {isIndividualSport && (
              <div className="form-row-gaming">
                <div className="form-group-gaming">
                  <label className="form-label-gaming">Play Format</label>
                  <select
                    name="play_format"
                    value={formData.play_format}
                    onChange={handleInputChange}
                    className="form-select-gaming"
                    disabled={hasEntries}
                  >
                    <option value="singles">🏸 Singles (1 vs 1)</option>
                    <option value="doubles">👥 Doubles (2 vs 2)</option>
                  </select>
                  <p style={{ color: '#64748b', fontSize: '0.8rem', marginTop: '0.4rem' }}>
                    {hasEntries
                      ? 'The play format cannot change once players or teams have joined.'
                      : playFormatHelp(formData.play_format)}
                  </p>
                </div>
              </div>
            )}

            <div className="form-group-gaming">
              <label htmlFor="description" className="form-label-gaming">Description</label>
              <textarea id="description" name="description" value={formData.description} onChange={handleInputChange} rows="3" className="form-textarea-gaming" />
            </div>
          </FloatingCard>

          {/* League Configuration */}
          <FloatingCard className="form-section-gaming">
            <h3 className="form-section-title">⚙️ League Configuration</h3>
            <div className="form-row-gaming">
              <div className="form-group-gaming">
                <label htmlFor="number_of_courts" className="form-label-gaming">Number of Courts *</label>
                <input type="number" id="number_of_courts" name="number_of_courts" value={formData.number_of_courts}
                  onChange={handleInputChange} min="1" max="10"
                  className={`form-input-gaming${errors.number_of_courts ? ' input-error-gaming' : ''}`} />
                {errors.number_of_courts && <span className="error-message-gaming">{errors.number_of_courts}</span>}
              </div>
              <div className="form-group-gaming">
                <label htmlFor="total_teams" className="form-label-gaming">Total Teams *</label>
                <input type="number" id="total_teams" name="total_teams" value={formData.total_teams}
                  onChange={handleInputChange} min="2" max="20"
                  className={`form-input-gaming${errors.total_teams ? ' input-error-gaming' : ''}`} />
                {errors.total_teams && <span className="error-message-gaming">{errors.total_teams}</span>}
              </div>
              <div className="form-group-gaming">
                <label htmlFor="games_per_team_per_day" className="form-label-gaming">Max Games per Day</label>
                <input type="number" id="games_per_team_per_day" name="games_per_team_per_day"
                  value={formData.games_per_team_per_day} onChange={handleInputChange}
                  min="1" max="10" placeholder="Optional" className="form-input-gaming" />
              </div>
            </div>
            <div className="form-row-gaming">
              <div className="form-group-gaming">
                <label htmlFor="playoff_format" className="form-label-gaming">Playoff Format</label>
                <select id="playoff_format" name="playoff_format" value={formData.playoff_format} onChange={handleInputChange} className="form-select-gaming">
                  <option value="single_elimination">🏆 Single Elimination</option>
                  <option value="double_elimination">🔄 Double Elimination</option>
                  <option value="round_robin">🔁 Round Robin</option>
                </select>
              </div>
              <div className="form-group-gaming">
                <label className="form-label-gaming">League Privacy</label>
                <div className="league-privacy-toggle">
                  <button type="button"
                    className={`privacy-option-btn${formData.is_public ? ' active' : ''}`}
                    onClick={() => setFormData(prev => ({ ...prev, is_public: true }))}>
                    🌐 Public
                  </button>
                  <button type="button"
                    className={`privacy-option-btn${!formData.is_public ? ' active' : ''}`}
                    onClick={() => setFormData(prev => ({ ...prev, is_public: false }))}>
                    🔒 Private (Invite-only)
                  </button>
                </div>
              </div>

              {/* Skill Level */}
              <div className="form-group-gaming">
                <label htmlFor="skill_level" className="form-label-gaming">Skill Level</label>
                <select
                  id="skill_level"
                  name="skill_level"
                  value={formData.skill_level || ""}
                  onChange={e => setFormData(prev => ({ ...prev, skill_level: e.target.value }))}
                  className="form-select-gaming"
                >
                  <option value="">Any skill level</option>
                  {SKILL_LEVEL_OPTIONS.map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>

              {/* How players pay: shown only while payments are on */}
              <LeagueFeeFields fee={fee} onChange={setFee} />
            </div>
          </FloatingCard>

          {/* Scoring / Match Rules */}
          <FloatingCard className="form-section-gaming">
            <h3 className="form-section-title">🎯 Match Rules</h3>
            <SportMatchConfig
              sportName={formData.sport_type}
              config={gameRules}
              onChange={setGameRules}
            />
          </FloatingCard>

          {/* Venue & Schedule */}
          <FloatingCard className="form-section-gaming">
            <h3 className="form-section-title">📍 Venue &amp; Schedule</h3>
            <div className="form-group-gaming">
              <VenueSearch
                label="League Venue"
                value={{ venue: formData.venue_name, venue_place_id: formData.venue_place_id, venue_address: formData.venue_address, venue_lat: formData.venue_lat, venue_lng: formData.venue_lng, court: '' }}
                onChange={(v) => setFormData((prev) => ({ ...prev, venue_name: v.venue, venue_address: v.venue_address, venue_lat: v.venue_lat, venue_lng: v.venue_lng, venue_place_id: v.venue_place_id }))}
              />
            </div>

            {/* Schedule approach toggle */}
            <div className="form-group-gaming">
              <label className="form-label-gaming">Schedule Approach</label>
              <div className="schedule-mode-toggle">
                <button type="button"
                  className={`mode-btn${scheduleMode === 'weekdays' ? ' active' : ''}`}
                  onClick={() => setScheduleMode('weekdays')}>
                  🔄 Recurring Weekdays
                </button>
                <button type="button"
                  className={`mode-btn${scheduleMode === 'specific_dates' ? ' active' : ''}`}
                  onClick={() => setScheduleMode('specific_dates')}>
                  📅 Specific Dates
                </button>
              </div>
            </div>

            {scheduleMode === 'weekdays' ? (
              <div className="form-group-gaming">
                <label className="form-label-gaming">Game Days <small style={{opacity:0.6}}>(repeats every week)</small></label>
                <div className="game-days-selector">
                  {DAYS_OF_WEEK.map(day => (
                    <button key={day} type="button"
                      className={`day-pill-btn${formData.game_days.includes(day) ? ' active' : ''}`}
                      onClick={() => handleDayToggle(day)}>
                      {day.slice(0, 3)}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="form-group-gaming">
                <label className="form-label-gaming">
                  Specific Game Dates
                  <small style={{opacity:0.6, marginLeft:'0.5rem'}}>(pick exact dates in your season)</small>
                </label>
                {(!formData.season_start_date || !formData.season_end_date) ? (
                  <p className="schedule-mode-hint">Set your season start and end dates first.</p>
                ) : (
                  <>
                    <div className="specific-dates-quickpick">
                      <span className="quickpick-label">Quick-pick all occurrences of:</span>
                      <div className="game-days-selector" style={{marginTop:'0.35rem'}}>
                        {DAYS_OF_WEEK.map(day => {
                          const datesForDay = getDatesForWeekday(formData.season_start_date, formData.season_end_date, day);
                          const allSel = datesForDay.length > 0 && datesForDay.every(d => specificDates.includes(d));
                          return (
                            <button key={day} type="button"
                              className={`day-pill-btn${allSel ? ' active' : ''}`}
                              onClick={() => handleQuickPickDay(day)}
                              disabled={datesForDay.length === 0}
                              title={`${datesForDay.length} ${day}s in season`}>
                              {day.slice(0, 3)}
                              <span style={{fontSize:'0.7rem',marginLeft:'2px',opacity:0.7}}>({datesForDay.length})</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div className="specific-dates-summary">
                      <span>{specificDates.length} date{specificDates.length !== 1 ? 's' : ''} selected</span>
                      {specificDates.length > 0 && (
                        <button type="button" className="day-pill-btn"
                          style={{marginLeft:'0.75rem',padding:'0.15rem 0.6rem',fontSize:'0.78rem'}}
                          onClick={() => setSpecificDates([])}>
                          Clear all
                        </button>
                      )}
                    </div>
                    {specificDates.length > 0 && (
                      <div className="specific-dates-chips">
                        {[...specificDates].sort().map(d => (
                          <span key={d} className="specific-date-chip">
                            {new Date(d + 'T00:00:00').toLocaleDateString('en-US',{weekday:'short',month:'short',day:'numeric'})}
                            <button type="button" className="chip-remove"
                              onClick={() => setSpecificDates(prev => prev.filter(x => x !== d))}>x</button>
                          </span>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <div className="form-row-gaming">
              <div className="form-group-gaming">
                <label htmlFor="start_time" className="form-label-gaming">Window Start</label>
                <input type="time" id="start_time" name="start_time" value={formData.start_time} onChange={handleInputChange} className="form-input-gaming" />
              </div>
              <div className="form-group-gaming">
                <label htmlFor="end_time" className="form-label-gaming">Window End</label>
                <input type="time" id="end_time" name="end_time" value={formData.end_time} onChange={handleInputChange} className="form-input-gaming" />
              </div>
            </div>
          </FloatingCard>

          {/* Season Dates */}
          <FloatingCard className="form-section-gaming">
            <h3 className="form-section-title">📅 Season Dates</h3>
            <div className="form-row-gaming">
              <div className="form-group-gaming">
                <label htmlFor="season_start_date" className="form-label-gaming">Season Start Date *</label>
                <input type="date" id="season_start_date" name="season_start_date" value={formData.season_start_date} onChange={handleInputChange}
                  className={`form-input-gaming${errors.season_start_date ? ' input-error-gaming' : ''}`} />
                {errors.season_start_date && <span className="error-message-gaming">{errors.season_start_date}</span>}
              </div>
              <div className="form-group-gaming">
                <label htmlFor="season_end_date" className="form-label-gaming">Season End Date *</label>
                <input type="date" id="season_end_date" name="season_end_date" value={formData.season_end_date} onChange={handleInputChange}
                  className={`form-input-gaming${errors.season_end_date ? ' input-error-gaming' : ''}`} />
                {errors.season_end_date && <span className="error-message-gaming">{errors.season_end_date}</span>}
              </div>
              <div className="form-group-gaming">
                <label htmlFor="registration_deadline" className="form-label-gaming">Registration Deadline</label>
                <input type="date" id="registration_deadline" name="registration_deadline" value={formData.registration_deadline} onChange={handleInputChange} className="form-input-gaming" />
              </div>
            </div>
          </FloatingCard>

          {/* Actions */}
          <FloatingCard className="form-section-gaming form-actions-gaming">
            <div className="form-actions-content">
              <button type="button" onClick={onCancel} className="floating-btn cancel-btn-gaming">
                ❌ Cancel
              </button>
              <button type="submit" disabled={loading} className="floating-btn create-btn-gaming">
                {loading ? 'Updating...' : '✅ Update League'}
              </button>
            </div>
          </FloatingCard>

        </form>
      </div>
    </GameLayout>
  );
};

export default LeagueEditForm;
